import React, { useState, useMemo, useEffect } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { userPropertiesShape } from 'ext/entities/user-properties';
import { Select, DebouncedInput as Input } from 'ext/components/ui';
import { BlockContentShape } from 'entities/block/shape';

const Row = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 10px;
`;

const ConditionsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-small);
  margin-bottom: var(--spacing-small);
`;

const FlexSelect = styled(Select)`
  min-width: auto !important;
  width: 100%;
`;

function Conditions({
  condition,
  allSurveyBlocks,
  userProperties,
  onActionsChange,
}) {
  const [clauseType, setClauseType] = useState('');
  const [selectedOption, setSelectedOption] = useState('');
  const [operator, setOperator] = useState('');
  const [comparisonValue, setComparisonValue] = useState('');

  // Set the state from the condition
  useEffect(() => {
    if (condition.survey) {
      const { block, operator: op, value } = condition.survey;
      setClauseType('survey');
      setSelectedOption(block);
      setOperator(op);
      setComparisonValue(value);
    } else if (condition.token) {
      const { token, operator: op, value } = condition.token;
      setClauseType('token');
      setSelectedOption(token.replace(/[{}]/g, ''));
      setOperator(op);
      setComparisonValue(value);
    }
  }, [condition]);

  const clauseOptions = [
    { value: 'survey', label: 'Survey question' },
    { value: 'token', label: 'User property' },
  ];

  const operatorOptions = [
    { value: '==', label: 'Equals' },
    { value: '!=', label: "Doesn't equal" },
    { value: '*', label: 'Contains' },
    { value: '!*', label: "Doesn't contain" },
    { value: '^', label: 'Starts with' },
    { value: '!^', label: "Doesn't start with" },
    { value: '$', label: 'Ends with' },
    { value: '!$', label: "Doesn't end with" },
    { value: 'regex', label: 'Matches regex' },
    { value: '>', label: 'Is greater than' },
    { value: '>=', label: 'Is greater than or equal' },
    { value: '<', label: 'Is less than' },
    { value: '<=', label: 'Is less than or equal' },
  ];

  const availableOptions = useMemo(() => {
    if (clauseType === 'survey') {
      return (
        allSurveyBlocks?.map(block => ({
          value: block.content.id,
          label: block.content.label.text || block.content.id,
        })) || []
      );
    }
    if (clauseType === 'token') {
      return userProperties.map(property => ({
        value: property.value,
        label: property.name,
      }));
    }
    return [];
  }, [clauseType, allSurveyBlocks, userProperties]);

  const handleConditionChange = (
    newClauseType = clauseType,
    newSelectedOption = selectedOption,
    newOperator = operator,
    newComparisonValue = comparisonValue
  ) => {
    onActionsChange({
      condition: {
        [newClauseType]: {
          ...(newClauseType === 'survey'
            ? { block: newSelectedOption }
            : { token: `{{${newSelectedOption}}}` }),
          operator: newOperator,
          value: newComparisonValue,
        },
      },
    });
  };

  const handleClauseTypeChange = selected => {
    if (selected.value === clauseType) {
      return;
    }
    setClauseType(selected.value);
    setSelectedOption('');
    handleConditionChange(selected.value, '', operator, comparisonValue);
  };

  const handleSelectedOptionChange = selected => {
    setSelectedOption(selected.value);
    handleConditionChange(
      clauseType,
      selected.value,
      operator,
      comparisonValue
    );
  };

  const handleOperatorChange = selected => {
    setOperator(selected.value);
    handleConditionChange(
      clauseType,
      selectedOption,
      selected.value,
      comparisonValue
    );
  };

  const handleComparisonValueChange = e => {
    setComparisonValue(e.target.value);
    handleConditionChange(clauseType, selectedOption, operator, e.target.value);
  };

  return (
    <ConditionsWrapper>
      <Select
        value={clauseOptions.find(({ value }) => value === clauseType)}
        onChange={handleClauseTypeChange}
        options={clauseOptions}
        placeholder="Select condition type"
      />
      <Select
        key={clauseType}
        value={availableOptions.find(({ value }) => value === selectedOption)}
        onChange={handleSelectedOptionChange}
        options={availableOptions}
        placeholder="Select an option"
      />
      <Row>
        <FlexSelect
          value={operatorOptions.find(({ value }) => value === operator)}
          onChange={handleOperatorChange}
          options={operatorOptions}
          placeholder="Select operator"
        />
        <Input
          type="text"
          defaultValue={comparisonValue}
          onChange={handleComparisonValueChange}
          placeholder="Enter value to compare"
          style={{ width: '100px' }}
        />
      </Row>
    </ConditionsWrapper>
  );
}

Conditions.propTypes = {
  condition: PropTypes.shape({
    survey: PropTypes.shape({
      block: PropTypes.string,
      operator: PropTypes.string,
      value: PropTypes.string,
    }),
    token: PropTypes.shape({
      token: PropTypes.string,
      operator: PropTypes.string,
      value: PropTypes.string,
    }),
  }),
  allSurveyBlocks: PropTypes.arrayOf(BlockContentShape),
  userProperties: userPropertiesShape,
  onActionsChange: PropTypes.func,
};

export default Conditions;
